import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getSessionId } from "../lib/api";
import { useAuth } from "./AuthContext";
import { saveCartSnapshot } from "../services/analytics";

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

// Fields preserved for gift-card line-items (so they survive cart sync).
const GIFT_FIELDS = [
  "is_gift_card", "gift_card_template_id", "gift_card_amount_azn",
  "multi_use", "recipient_name", "recipient_email", "gift_message",
];

function compactItem(i) {
  if (i.is_gift_card) {
    const base = {
      product_id: i.product_id, qty: i.qty,
      title: i.title, artist_name: i.artist_name || "",
      cover_url: i.cover_url || "", price_azn: i.price_azn,
    };
    for (const k of GIFT_FIELDS) if (i[k] !== undefined) base[k] = i[k];
    return base;
  }
  return { product_id: i.product_id, qty: i.qty };
}

export function CartProvider({ children }) {
  const { user, profile, authChecked } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const sessionId = user ? `u_${user.uid}` : getSessionId();

  useEffect(() => {
    if (!authChecked) return;
    api.get(`/cart/${sessionId}`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [authChecked, sessionId]);

  const sync = useCallback(async (newItems) => {
    setItems(newItems);
    try {
      await api.post(`/cart`, {
        session_id: sessionId,
        items: newItems.map(compactItem),
      });
    } catch (_e) { /* noop */ }
    // Mirror to Firestore so admin can see live carts.
    if (user) {
      saveCartSnapshot(sessionId, {
        userId: user.uid,
        userEmail: profile?.email || user.email,
        items: newItems,
      });
    }
  }, [sessionId, user, profile]);

  const add = useCallback((product, qty = 1) => {
    const exists = items.find((i) => i.product_id === product.id);
    const discPct = Math.max(0, Math.min(90, Number(product.discount_pct) || 0));
    const origPrice = Number(product.price_azn) || 0;
    const effectivePrice = discPct > 0 ? origPrice * (1 - discPct / 100) : origPrice;
    let next;
    if (exists) {
      next = items.map((i) => i.product_id === product.id ? { ...i, qty: i.qty + qty } : i);
    } else {
      next = [...items, {
        product_id: product.id, title: product.title,
        artist_name: product.artist_name || "",
        cover_url: product.cover_url, price_azn: effectivePrice, qty,
        original_price_azn: origPrice, discount_pct: discPct,
      }];
    }
    sync(next);
    setOpen(true);
  }, [items, sync]);

  // Each gift-card add is its own line item (unique synthetic product_id),
  // so quantity is always 1 and lines never merge.
  const addGiftCard = useCallback((template, opts = {}) => {
    const giftId = `gc_${template.id}_${Math.random().toString(36).slice(2, 10)}`;
    const amount = Number(template.amount_azn) || 0;
    const next = [...items, {
      product_id: giftId,
      title: `${template.name} — Hədiyyə Kartı`,
      artist_name: "",
      cover_url: template.image_url || "",
      price_azn: amount,
      qty: 1,
      is_gift_card: true,
      gift_card_template_id: template.id,
      gift_card_amount_azn: amount,
      multi_use: template.multi_use !== false,
      recipient_name: opts.recipient_name || "",
      recipient_email: opts.recipient_email || "",
      gift_message: opts.gift_message || "",
    }];
    sync(next);
    setOpen(true);
  }, [items, sync]);

  const setQty = useCallback((pid, qty) => {
    const found = items.find((i) => i.product_id === pid);
    if (found?.is_gift_card) return; // gift cards always qty 1
    if (qty <= 0) sync(items.filter((i) => i.product_id !== pid));
    else sync(items.map((i) => i.product_id === pid ? { ...i, qty } : i));
  }, [items, sync]);

  const remove = useCallback((pid) => sync(items.filter((i) => i.product_id !== pid)), [items, sync]);
  const clear = useCallback(() => sync([]), [sync]);

  const subtotalAzn = items.reduce((s, i) => s + i.price_azn * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartCtx.Provider value={{ items, count, subtotalAzn, add, addGiftCard, setQty, remove, clear, open, setOpen, sessionId }}>
      {children}
    </CartCtx.Provider>
  );
}
